import type { ActionType } from '@ant-design/pro-components';
import { ProTable } from '@ant-design/pro-components';
import { Button, message, Modal, Table } from 'antd';
import React, { useImperativeHandle, useRef, useState } from 'react';
import { getLogDetail, getLogList } from '@/services/ant-design-pro/api';

export interface LogViewerProps {
  /** 模型类型，如 'CHANNEL', 'ADVERT', 'DELIVERY' 等 */
  model: string;
  /** 实体ID */
  entityId: number | null;
  /** 是否显示日志弹窗 */
  visible?: boolean;
  /** 弹窗关闭回调 */
  onClose?: () => void;
}

export interface LogViewerRef {
  /** 打开日志弹窗 */
  open: (entityId: number) => void;
}

/**
 * 日志查看器组件
 * 包含日志列表和详情功能
 */
const LogViewer = React.forwardRef<LogViewerRef, LogViewerProps>(
  ({ model, entityId: propEntityId, visible: propVisible, onClose }, ref) => {
  const [logModalVisible, setLogModalVisible] = useState<boolean>(false);
  const [currentEntityId, setCurrentEntityId] = useState<number | null>(null);
  const logActionRef = useRef<ActionType | null>(null);
  const [logDetailModalVisible, setLogDetailModalVisible] = useState<boolean>(false);
  const [logDetailData, setLogDetailData] = useState<API.LogDetailField[]>([]);
  const [loadingDetail, setLoadingDetail] = useState<boolean>(false);

  // 使用 useImperativeHandle 暴露方法给父组件
  useImperativeHandle(ref, () => ({
    open: (entityId: number) => {
      setCurrentEntityId(entityId);
      setLogModalVisible(true);
    },
  }));

  // 如果传入了 visible 和 entityId props，使用受控模式
  const isControlled = propVisible !== undefined && propEntityId !== null;
  const visible = isControlled ? propVisible : logModalVisible;
  const entityId = isControlled ? propEntityId : currentEntityId;

  const handleClose = () => {
    if (isControlled) {
      onClose?.();
    } else {
      setLogModalVisible(false);
      setCurrentEntityId(null);
    }
  };

  // 格式化时间戳
  const formatDateTime = (timestamp: number | undefined): string => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  // 格式化操作类型
  const formatOperate = (operate: string | undefined): string => {
    if (!operate) return '-';
    const operateMap: { [key: string]: string } = {
      UPDATE: '更新',
      CREATE: '创建',
      DELETE: '删除',
    };
    return operateMap[operate] || operate;
  };

  // 处理查看详情
  const handleViewDetail = async (logId: number) => {
    setLoadingDetail(true);
    setLogDetailModalVisible(true);
    try {
      const response = await getLogDetail(logId);
      if (response.code === 0 && response.data?.data) {
        try {
          const parsedData = JSON.parse(response.data.data) as API.LogDetailField[];
          // 按照 order 字段排序
          const sortedData = parsedData.sort((a, b) => (a.order || 0) - (b.order || 0));
          setLogDetailData(sortedData);
        } catch (parseError) {
          console.error('解析日志数据失败', parseError);
          message.error('解析日志数据失败');
          setLogDetailData([]);
        }
      } else {
        message.error(response.message || '获取日志详情失败');
        setLogDetailData([]);
      }
    } catch (error) {
      message.error('请求失败，请重试');
      setLogDetailData([]);
    } finally {
      setLoadingDetail(false);
    }
  };

  return (
    <>
      <Modal
        title="日志"
        open={visible}
        onCancel={handleClose}
        footer={[
          <Button key="cancel" onClick={handleClose}>
            取消
          </Button>,
          <Button key="ok" type="primary" onClick={handleClose}>
            确定
          </Button>,
        ]}
        width={1000}
        destroyOnHidden
      >
        <ProTable<API.LogItem>
          actionRef={logActionRef}
          rowKey="logId"
          search={false}
          toolBarRender={false}
          request={async (params) => {
            if (!entityId) {
              return {
                data: [],
                success: false,
                total: 0,
              };
            }
            try {
              const response = await getLogList({
                model,
                entityid: entityId,
                pageNum: params.current || 1,
                pageSize: params.pageSize || 10,
              });
              if (response.code === 0 && response.data) {
                return {
                  data: response.data.list || [],
                  success: true,
                  total: response.data.total || 0,
                };
              } else {
                message.error(response.message || '获取日志失败');
                return {
                  data: [],
                  success: false,
                  total: 0,
                };
              }
            } catch (error) {
              message.error('请求失败，请重试');
              return {
                data: [],
                success: false,
                total: 0,
              };
            }
          }}
          columns={[
            {
              title: '序号',
              dataIndex: 'logId',
              width: 100,
              align: 'center',
            },
            {
              title: '用户名',
              dataIndex: 'username',
              width: 120,
              ellipsis: true,
            },
            {
              title: '操作时间',
              dataIndex: 'logTime',
              width: 180,
              render: (_, record) => formatDateTime(record.logTime),
            },
            {
              title: '操作类型',
              dataIndex: 'operate',
              width: 120,
              render: (text: any) => formatOperate(text),
            },
            {
              title: '操作',
              valueType: 'option',
              width: 100,
              render: (_, record) => [
                <a
                  key="detail"
                  onClick={() => {
                    if (record.logId) {
                      handleViewDetail(record.logId);
                    }
                  }}
                >
                  详情
                </a>,
              ],
            },
          ]}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          size="small"
        />
      </Modal>
      <Modal
        title="编辑"
        open={logDetailModalVisible}
        onCancel={() => {
          setLogDetailModalVisible(false);
          setLogDetailData([]);
        }}
        footer={[
          <Button
            key="ok"
            type="primary"
            onClick={() => {
              setLogDetailModalVisible(false);
              setLogDetailData([]);
            }}
          >
            确定
          </Button>,
        ]}
        width={1200}
        destroyOnHidden
      >
        <Table<API.LogDetailField>
          loading={loadingDetail}
          dataSource={logDetailData}
          rowKey={(record, index) => `field-${index}`}
          pagination={false}
          size="small"
          columns={[
            {
              title: '字段',
              dataIndex: 'key',
              width: 150,
              render: (text: string, record: API.LogDetailField) => {
                // 如果 oldValue 和 newValue 不同，高亮显示
                const hasChange = record.oldValue !== record.newValue;
                return hasChange ? (
                  <span style={{ color: '#ff4d4f', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{text}</span>
                ) : (
                  <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{text}</span>
                );
              },
            },
            {
              title: '更新前',
              dataIndex: 'oldValue',
              width: 400,
              render: (text: string) => (
                <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {text || '-'}
                </span>
              ),
            },
            {
              title: '更新后',
              dataIndex: 'newValue',
              width: 400,
              render: (text: string, record: API.LogDetailField) => {
                const hasChange = record.oldValue !== record.newValue;
                return (
                  <span style={hasChange ? { color: '#52c41a', whiteSpace: 'pre-wrap', wordBreak: 'break-word' } : { whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {text || '-'}
                  </span>
                );
              },
            },
          ]}
        />
      </Modal>
    </>
  );
});

LogViewer.displayName = 'LogViewer';

export default LogViewer;
